'use client';

import Script from 'next/script';
import { useEffect, useState } from 'react';

interface OptimizedAnalyticsProps {
  gtmId?: string;
  gaIdMain?: string;
  gaIdSecondary?: string;
  delayMs?: number; 
}


export function OptimizedAnalytics({
  gtmId,
  gaIdMain,
  gaIdSecondary,
  delayMs = 2000, 
}: OptimizedAnalyticsProps) {
  const [shouldLoad, setShouldLoad] = useState(false);

  useEffect(() => {

    const loadAnalytics = () => {
      setShouldLoad(true);
      cleanup();
    };

    const events = ['mousedown', 'touchstart', 'scroll', 'keydown'];

    events.forEach(event => {
      window.addEventListener(event, loadAnalytics, { passive: true, once: true });
    });
    const timer = setTimeout(loadAnalytics, delayMs);

    const cleanup = () => {
      clearTimeout(timer);
      events.forEach(event => {
        window.removeEventListener(event, loadAnalytics);
      });
    };

    return cleanup;
  }, [delayMs]);

  if (!shouldLoad) {
    return null;
  }

  return (
    <>
      <Script
        id="google-consent-mode"
        strategy="afterInteractive"
        dangerouslySetInnerHTML={{
          __html: `
            window.dataLayer = window.dataLayer || [];
            function gtag(){dataLayer.push(arguments);}
            
            gtag('consent', 'default', {
              'ad_storage': 'denied',
              'ad_user_data': 'denied',
              'ad_personalization': 'denied',
              'analytics_storage': 'denied',
              'wait_for_update': 500
            });
          `,
        }}
      />

      {gtmId && (
        <>
          <Script
            id="gtm-script"
            strategy="afterInteractive"
            dangerouslySetInnerHTML={{
              __html: `
                (function(w,d,s,l,i){w[l]=w[l]||[];w[l].push({'gtm.start':
                new Date().getTime(),event:'gtm.js'});var f=d.getElementsByTagName(s)[0],
                j=d.createElement(s),dl=l!='dataLayer'?'&l='+l:'';j.async=true;j.src=
                'https://www.googletagmanager.com/gtm.js?id='+i+dl;f.parentNode.insertBefore(j,f);
                })(window,document,'script','dataLayer','${gtmId}');
              `,
            }}
          />
          <noscript>
            <iframe
              src={`https://www.googletagmanager.com/ns.html?id=${gtmId}`}
              height="0"
              width="0"
              style={{ display: 'none', visibility: 'hidden' }}
            />
          </noscript>
        </>
      )}

      {gaIdMain && (
        <>
          <Script
            src={`https://www.googletagmanager.com/gtag/js?id=${gaIdMain}`}
            strategy="afterInteractive"
          />
          <Script
            id="ga-main-script"
            strategy="afterInteractive"
            dangerouslySetInnerHTML={{
              __html: `
                window.dataLayer = window.dataLayer || [];
                function gtag(){dataLayer.push(arguments);}
                gtag('js', new Date());
                gtag('config', '${gaIdMain}', {
                  page_path: window.location.pathname,
                  send_page_view: false
                });
              `,
            }}
          />
        </>
      )}

      {gaIdSecondary && (
        <Script
          id="ga-secondary-script"
          strategy="afterInteractive"
          dangerouslySetInnerHTML={{
            __html: `
              gtag('config', '${gaIdSecondary}', {
                page_path: window.location.pathname,
                send_page_view: false
              });
            `,
          }}
        />
      )}
    </>
  );
}
